import { useState } from 'preact/hooks';

interface LinkFormProps {
  onLink: (input: {
    service_code: string;
    username: string;
    password: string;
  }) => Promise<void>;
}

const SERVICE_OPTIONS = [
  { code: 'EC', label: 'Easee (EV Charger)' },
  { code: 'PP', label: 'PlugPay (EV Charger)' },
  { code: 'VC', label: 'Virtual (IoT Relay)' },
];

export function LinkForm({ onLink }: LinkFormProps) {
  const [serviceCode, setServiceCode] = useState('EC');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showUsername, setShowUsername] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const needsPassword = serviceCode !== 'VC';

  async function handleSubmit(e: Event) {
    e.preventDefault();
    if (!username.trim()) {
      setError('Username is required');
      return;
    }
    if (needsPassword && !password) {
      setError('Password is required for this service');
      return;
    }

    setError(null);
    setSubmitting(true);
    try {
      await onLink({
        service_code: serviceCode,
        username: username.trim(),
        password,
      });
      setUsername('');
      setPassword('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to link device');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form class="card link-form" onSubmit={handleSubmit}>
      <h3 class="link-form-title">Link a charger</h3>

      <div class="field">
        <label htmlFor="service-code">Service</label>
        <select
          id="service-code"
          value={serviceCode}
          onChange={(e) =>
            setServiceCode((e.target as HTMLSelectElement).value)
          }
        >
          {SERVICE_OPTIONS.map((opt) => (
            <option value={opt.code}>{opt.label}</option>
          ))}
        </select>
      </div>

      <div class="field">
        <div class="field-label-row">
          <label htmlFor="username">
            {serviceCode === 'VC' ? 'Device name / ID' : 'Email or username'}
          </label>
          {needsPassword && (
            <button
              type="button"
              class="toggle-visibility"
              onClick={() => setShowUsername(!showUsername)}
              aria-label={showUsername ? 'Hide username' : 'Show username'}
              data-testid="toggle-username"
            >
              {showUsername ? '🙈' : '👁'}
            </button>
          )}
        </div>
        <input
          id="username"
          type={showUsername || !needsPassword ? 'text' : 'password'}
          value={username}
          onInput={(e) =>
            setUsername((e.target as HTMLInputElement).value)
          }
          autocomplete="username"
          disabled={submitting}
        />
      </div>

      {needsPassword && (
        <div class="field">
          <div class="field-label-row">
            <label htmlFor="password">Password</label>
            <button
              type="button"
              class="toggle-visibility"
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              data-testid="toggle-password"
            >
              {showPassword ? '🙈' : '👁'}
            </button>
          </div>
          <input
            id="password"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onInput={(e) =>
              setPassword((e.target as HTMLInputElement).value)
            }
            autocomplete="current-password"
            disabled={submitting}
          />
        </div>
      )}

      {error && <div class="alert alert-error">{error}</div>}

      <button
        type="submit"
        class="btn btn-block"
        disabled={submitting || !username.trim()}
      >
        {submitting ? 'Linking…' : 'Link charger'}
      </button>
    </form>
  );
}
